import React from "react";
import { useTranslation } from "react-i18next";
import { Country, State } from "country-state-city";
import { truncateCommaSeparatedString } from "../../components/common/utlity/utility";
import numberToWord from "../../components/common/utlity/invoiceword";
import { AWS_URL } from "../../services/http.service";
import { getCurrencySymbol } from "../../components/common/utlity/options";
import { useSelector } from "react-redux";

const formatCurrency = (val, currency) => {
  let num = 0;
  if (typeof val === "number") {
    num = val;
  } else if (typeof val === "string") {
    num = parseFloat(val);
    if (isNaN(num)) num = 0;
  }
  return `${getCurrencySymbol(currency)} ${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (str) => {
  if (!str) return "-";
  const date = new Date(str);
  if (isNaN(date)) return str;
  return date.toLocaleDateString();
};

const TraditionalInvoice = ({
  item,
  containerRef,
  businessName,
  phone,
  darkMode = false,
  bussiness,
  selectedColor = "bg-blue-500",
}) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user.user);

  if (!item)
    return <div className='text-center'>No invoice data available</div>;

  const country = Country.getCountryByCode(bussiness?.country);
  const state = State.getStateByCodeAndCountry(
    bussiness?.state,
    bussiness?.country
  );
  const itemCountry = Country.getCountryByCode(item?.country);
  const itemState = State.getStateByCodeAndCountry(item?.state, item?.country);
  const isIntraState = bussiness?.state === item?.state;

  const calculateItemAmount = (item) => {
    const quantity = Number(item.quantity) || 0;
    const salePrice = Number(item.salePrice) || 0;
    const discountValue = parseFloat(item.discount || 0);
    const discountUnit = item.discountUnit || "%";
    const itemTotal = quantity * salePrice;
    let discountAmount = 0;
    if (
      discountUnit === "₹" ||
      discountUnit === "INR" ||
      discountUnit === "amount"
    ) {
      discountAmount = discountValue;
    } else {
      discountAmount = (itemTotal * discountValue) / 100;
    }
    return itemTotal - discountAmount;
  };

  const itemsToUse = item?.invoiceId?.startsWith("RT")
    ? item?.returnitems
    : item?.items;
  const totalQuantity =
    itemsToUse?.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0) || 0;
  const totalAmount =
    itemsToUse?.reduce(
      (sum, i) => sum + (Number(i.quantity) * Number(i.salePrice) || 0),
      0
    ) || 0;
  const paidAmount =
    (parseFloat(item.transactionAmount) || 0) -
    (parseFloat(item.remainingTransactionAmount) || 0);
  const remainingBalance = item.remainingTransactionAmount || 0;

  const totalItemDiscount =
    itemsToUse?.reduce((sum, i) => {
      const discountValue = parseFloat(i.discount || 0);
      const discountUnit = i.discountUnit || "%";
      const quantity = parseFloat(i.quantity || 0);
      const salePrice = parseFloat(i.salePrice || 0);
      const itemTotal = quantity * salePrice;
      let discountAmount = 0;
      if (
        discountUnit === "₹" ||
        discountUnit === "INR" ||
        discountUnit === "amount"
      ) {
        discountAmount = discountValue;
      } else {
        discountAmount = (itemTotal * discountValue) / 100;
      }
      return sum + discountAmount;
    }, 0) || 0;

  const totalDiscount = totalItemDiscount;
  const isNonGSTInvoice = item?.showGST === false;
  const isReturnInvoice = item?.invoiceId?.startsWith("RT");
  const isEstimate = item?.saleType === "estimate";
  const isAdvancePayment = item?.saleType === "advancePayment";

  const hsnSummary = itemsToUse?.reduce((acc, item) => {
    const hsn = item.hsnNumber || item.hsn || "N/A";
    const taxableValue = Number(item.quantity) * Number(item.salePrice) || 0;
    const isNoGSTItem =
      isNonGSTInvoice ||
      (isIntraState &&
        (!item.cgst || parseFloat(item.cgst) === 0) &&
        (!item.sgst_utgst || parseFloat(item.sgst_utgst) === 0)) ||
      (!isIntraState && (!item.igst || parseFloat(item.igst) === 0));
    let totalGstRate = 0;
    let taxAmount = 0;
    if (!isNoGSTItem) {
      if (isIntraState) {
        totalGstRate =
          parseFloat(item.cgst || 0) + parseFloat(item.sgst_utgst || 0);
      } else {
        totalGstRate = parseFloat(item.igst || 0);
      }
      taxAmount = (taxableValue * totalGstRate) / 100;
    }
    if (!acc[hsn]) {
      acc[hsn] = {
        hsn: hsn,
        taxableValue: 0,
        cgstRate: isIntraState && !isNoGSTItem ? parseFloat(item.cgst || 0) : 0,
        sgstRate:
          isIntraState && !isNoGSTItem ? parseFloat(item.sgst_utgst || 0) : 0,
        igstRate:
          !isIntraState && !isNoGSTItem ? parseFloat(item.igst || 0) : 0,
        cessRate: parseFloat(item.cess || 0),
        cgstAmount: 0,
        sgstAmount: 0,
        igstAmount: 0,
        cessAmount: 0,
        totalTax: 0,
        totalGstRate: 0,
        isNoGST: isNoGSTItem,
      };
    }
    acc[hsn].taxableValue += taxableValue;
    acc[hsn].totalTax += taxAmount;
    acc[hsn].totalGstRate = totalGstRate;
    if (!isNoGSTItem) {
      if (isIntraState) {
        acc[hsn].cgstAmount +=
          (taxableValue * parseFloat(item.cgst || 0)) / 100;
        acc[hsn].sgstAmount +=
          (taxableValue * parseFloat(item.sgst_utgst || 0)) / 100;
      } else {
        acc[hsn].igstAmount +=
          (taxableValue * parseFloat(item.igst || 0)) / 100;
      }
    }
    acc[hsn].cessAmount += (taxableValue * parseFloat(item.cess || 0)) / 100;
    return acc;
  }, {});

  const hsnSummaryArray = Object.values(hsnSummary || {});
  const totalTaxAmount = hsnSummaryArray.reduce(
    (sum, hsn) => sum + hsn.totalTax + hsn.cessAmount,
    0
  );
  const grandTotalBeforeDiscount = totalAmount + totalTaxAmount;
  const grandTotal = grandTotalBeforeDiscount - totalDiscount;
  const amountInwords = numberToWord(grandTotal);

  let currentAmount = grandTotal;
  const totalAdditionalDiscount = Array.isArray(item.additionalDiscount)
    ? item.additionalDiscount.reduce((sum, d) => {
        const amount = parseFloat(d.amount) || 0;
        let discountAmount = 0;
        if (d.type === "discount") {
          discountAmount = (currentAmount * amount) / 100;
        } else {
          discountAmount = amount;
        }
        currentAmount -= discountAmount;
        return sum + discountAmount;
      }, 0)
    : 0;

  const totalAdditionalFee = Array.isArray(item.additionalFee)
    ? item.additionalFee.reduce((sum, d) => {
        const amount = parseFloat(d.amount) || 0;
        let feeAmount = 0;
        if (d.type === "discount") {
          feeAmount = (currentAmount * amount) / 100;
        } else {
          feeAmount = amount;
        }
        currentAmount += feeAmount;
        return sum + feeAmount;
      }, 0)
    : 0;

  const isEntireInvoiceNoGST =
    isNonGSTInvoice || hsnSummaryArray.every((hsn) => hsn.isNoGST);

  return (
    <div
      ref={containerRef}
      className={`max-w-[210mm] mx-auto p-4 ${
        darkMode ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900"
      }`}
      style={{ width: "210mm" }}
    >
      {/* Header */}
      <div className='mb-4 text-center'>
        <img
          src={
            bussiness?.businesslogo
              ? AWS_URL + bussiness.businesslogo
              : "https://via.placeholder.com/50"
          }
          alt='Business Logo'
          className='w-16 h-16 mx-auto mb-2'
          // crossOrigin='anonymous'
        />
        <h1 className='text-xl font-bold'>{businessName || "Business Name"}</h1>
        <p className='text-sm'>
          Address:{" "}
          {`${bussiness.firstLine}, ${state?.name}, ${country?.name}` || "N/A"}
        </p>
        <p className='text-sm'>Phone: {phone || "N/A"}</p>
        {!isNonGSTInvoice && (
          <p className='text-sm'>GSTIN: {bussiness.gstNumber || "N/A"}</p>
        )}
        <p className='text-sm'>
          {isReturnInvoice
            ? "Return Invoice"
            : isEstimate
            ? "Estimate"
            : "Invoice"}
        </p>
        {isNonGSTInvoice && !isEstimate && (
          <p
            className={`text-sm ${darkMode ? "text-red-400" : "text-red-600"}`}
          >
            Non-GST Invoice
          </p>
        )}
        {isAdvancePayment && (
          <p
            className={`text-sm ${
              darkMode ? "text-blue-400" : "text-blue-600"
            }`}
          >
            Proforma Invoice
          </p>
        )}
      </div>

      {/* Billing and Invoice Details */}
      <div className='mb-4'>
        <table className='w-full text-sm'>
          <tbody>
            <tr>
              <td className='py-1'>
                <strong>Bill To:</strong> {item.customerName || "-"}
                <br />
                {item.address}, {itemState?.name}, {itemCountry?.name || "N/A"}
                <br />
                Contact: {item.mobile || "-"}
              </td>
              <td className='py-1 text-right'>
                <strong>Invoice No:</strong> {item.invoiceId || "-"}
                <br />
                <strong>Date:</strong> {formatDate(item.saleDate)}
                <br />
                {item?.committedDateToPay && (
                  <span className={darkMode ? "text-red-400" : "text-red-600"}>
                    Due Date: {formatDate(item.committedDateToPay)}
                  </span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Items Table */}
      <div className='mb-4'>
        <table className='w-full border border-gray-400 text-sm'>
          <thead className={darkMode ? "bg-gray-800" : "bg-gray-200"}>
            <tr>
              <th className='border py-1 px-2'>Sl No.</th>
              <th className='border py-1 px-2'>Description</th>
              <th className='border py-1 px-2'>HSN/SAC</th>
              {!isEntireInvoiceNoGST && (
                <th className='border py-1 px-2'>GST</th>
              )}
              <th className='border py-1 px-2'>Qty</th>
              <th className='border py-1 px-2'>Rate</th>
              <th className='border py-1 px-2'>Unit</th>
              <th className='border py-1 px-2'>Discount</th>
              <th className='border py-1 px-2'>Amount</th>
            </tr>
          </thead>
          <tbody>
            {itemsToUse?.map((itm, idx) => {
              const itemAmount =
                Number(itm.quantity) * Number(itm.salePrice) || 0;
              const discountRate = parseFloat(itm.discount || 0);
              const discountUnit = itm.discountUnit || "%";
              let discountAmount = 0;
              if (
                discountUnit === "₹" ||
                discountUnit === "INR" ||
                discountUnit === "amount"
              ) {
                discountAmount = discountRate;
              } else {
                discountAmount = (itemAmount * discountRate) / 100;
              }
              const isNoGSTItem =
                isNonGSTInvoice ||
                (isIntraState &&
                  (!itm.cgst || parseFloat(itm.cgst) === 0) &&
                  (!itm.sgst_utgst || parseFloat(itm.sgst_utgst) === 0)) ||
                (!isIntraState && (!itm.igst || parseFloat(itm.igst) === 0));
              const gstRate = isIntraState
                ? parseFloat(itm.cgst || 0) + parseFloat(itm.sgst_utgst || 0)
                : parseFloat(itm.igst || 0);
              const gstAmount = !isNoGSTItem ? (itemAmount * gstRate) / 100 : 0;

              return (
                <tr key={idx}>
                  <td className='border py-1 px-2'>{idx + 1}</td>
                  <td className='border py-1 px-2'>
                    {itm.productName || itm.description || "-"}
                  </td>
                  <td className='border py-1 px-2 text-center'>
                    {truncateCommaSeparatedString(itm.hsnNumber) ||
                      itm.hsn ||
                      "N/A"}
                  </td>
                  {!isEntireInvoiceNoGST && (
                    <td className='border py-1 px-2 text-center'>
                      {isNoGSTItem
                        ? "-"
                        : `${gstRate}% (${formatCurrency(
                            gstAmount,
                            bussiness?.currency
                          )})`}
                    </td>
                  )}
                  <td className='border py-1 px-2 text-center'>
                    {itm.quantity || 0}
                  </td>
                  <td className='border py-1 px-2 text-right'>
                    {formatCurrency(itm.salePrice, bussiness?.currency)}
                  </td>
                  <td className='border py-1 px-2 text-center'>
                    {itm.unitSize || "Pcs"}
                  </td>
                  <td className='border py-1 px-2 text-center'>
                    {discountRate > 0
                      ? `${
                          discountUnit === "₹" ||
                          discountUnit === "INR" ||
                          discountUnit === "amount"
                            ? formatCurrency(discountRate, bussiness?.currency)
                            : `${discountRate}%`
                        } (-${formatCurrency(
                          discountAmount,
                          bussiness?.currency
                        )})`
                      : "-"}
                  </td>
                  <td className='border py-1 px-2 text-right'>
                    {formatCurrency(itemAmount, bussiness?.currency)}
                  </td>
                </tr>
              );
            })}
            <tr>
              <td
                colSpan={isEntireInvoiceNoGST ? 7 : 8}
                className='border py-1 px-2 text-right'
              >
                Total Amount
              </td>
              <td className='border py-1 px-2 text-right'>
                {formatCurrency(totalAmount, bussiness?.currency)}
              </td>
            </tr>
            {!isEntireInvoiceNoGST && (
              <>
                {isIntraState &&
                  hsnSummaryArray.reduce(
                    (sum, hsn) => sum + hsn.cgstAmount,
                    0
                  ) > 0 && (
                    <tr>
                      <td colSpan={8} className='border py-1 px-2 text-right'>
                        CGST
                      </td>
                      <td className='border py-1 px-2 text-right'>
                        {formatCurrency(
                          hsnSummaryArray.reduce(
                            (sum, hsn) => sum + hsn.cgstAmount,
                            0
                          ),
                          bussiness?.currency
                        )}
                      </td>
                    </tr>
                  )}
                {isIntraState &&
                  hsnSummaryArray.reduce(
                    (sum, hsn) => sum + hsn.sgstAmount,
                    0
                  ) > 0 && (
                    <tr>
                      <td colSpan={8} className='border py-1 px-2 text-right'>
                        SGST
                      </td>
                      <td className='border py-1 px-2 text-right'>
                        {formatCurrency(
                          hsnSummaryArray.reduce(
                            (sum, hsn) => sum + hsn.sgstAmount,
                            0
                          ),
                          bussiness?.currency
                        )}
                      </td>
                    </tr>
                  )}
                {!isIntraState &&
                  hsnSummaryArray.reduce(
                    (sum, hsn) => sum + hsn.igstAmount,
                    0
                  ) > 0 && (
                    <tr>
                      <td colSpan={8} className='border py-1 px-2 text-right'>
                        IGST
                      </td>
                      <td className='border py-1 px-2 text-right'>
                        {formatCurrency(
                          hsnSummaryArray.reduce(
                            (sum, hsn) => sum + hsn.igstAmount,
                            0
                          ),
                          bussiness?.currency
                        )}
                      </td>
                    </tr>
                  )}
                {hsnSummaryArray.reduce((sum, hsn) => sum + hsn.cessAmount, 0) >
                  0 && (
                  <tr>
                    <td colSpan={8} className='border py-1 px-2 text-right'>
                      Cess
                    </td>
                    <td className='border py-1 px-2 text-right'>
                      {formatCurrency(
                        hsnSummaryArray.reduce(
                          (sum, hsn) => sum + hsn.cessAmount,
                          0
                        ),
                        bussiness?.currency
                      )}
                    </td>
                  </tr>
                )}
              </>
            )}
            {totalDiscount > 0 && (
              <tr>
                <td
                  colSpan={isEntireInvoiceNoGST ? 7 : 8}
                  className='border py-1 px-2 text-right'
                >
                  Total Discount
                </td>
                <td className='border py-1 px-2 text-right'>
                  -{formatCurrency(totalDiscount, bussiness?.currency)}
                </td>
              </tr>
            )}
            <tr>
              <td
                colSpan={isEntireInvoiceNoGST ? 7 : 8}
                className='border py-1 px-2 text-right'
              >
                Grand Total
              </td>
              <td className='border py-1 px-2 text-right'>
                {formatCurrency(grandTotal, bussiness?.currency)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className='mb-4'>
        <table className='w-full text-sm'>
          <tbody>
            <tr>
              <td className='py-1'>
                Amount in Words: {numberToWord(grandTotal)} Rupees only
              </td>
            </tr>
            {totalAdditionalDiscount > 0 && (
              <tr>
                <td className='py-1'>
                  Additional Discounts:
                  {item.additionalDiscount?.map((discount, idx) => (
                    <span key={idx}>
                      {" "}
                      {discount.label}: {discount.amount}{" "}
                      {discount.type === "discount" ? "%" : "₹"}
                    </span>
                  ))}{" "}
                  (-
                  {formatCurrency(totalAdditionalDiscount, bussiness?.currency)}
                  )
                </td>
              </tr>
            )}
            {totalAdditionalFee > 0 && (
              <tr>
                <td className='py-1'>
                  Additional Fees:
                  {item.additionalFee?.map((fee, idx) => (
                    <span key={idx}>
                      {" "}
                      {fee.label}: {fee.amount}{" "}
                      {fee.type === "discount" ? "%" : "₹"}
                    </span>
                  ))}{" "}
                  (+{formatCurrency(totalAdditionalFee, bussiness?.currency)})
                </td>
              </tr>
            )}
            {item.subscriptionDisc > 0 && (
              <tr>
                <td className='py-1'>
                  Subscription Savings: -
                  {formatCurrency(item.subscriptionDisc, bussiness?.currency)}
                </td>
              </tr>
            )}
            <tr>
              <td className='py-1'>
                <strong>Total Amount:</strong>{" "}
                {formatCurrency(
                  grandTotal -
                    totalAdditionalDiscount +
                    totalAdditionalFee -
                    (item.subscriptionDisc || 0),
                  bussiness?.currency
                )}
              </td>
            </tr>
            {(isAdvancePayment || item?.saleType === "advanceSale") && (
              <tr>
                <td className='py-1'>
                  Advance Amount:{" "}
                  {formatCurrency(item.advancePayment, bussiness?.currency)}
                </td>
              </tr>
            )}
            <tr>
              <td className='py-1'>Payment Mode: {item.paymentMode || "-"}</td>
            </tr>
            <tr>
              <td className='py-1'>
                {isAdvancePayment ? "Remaining Balance" : "Total Value"}:{" "}
                {isReturnInvoice
                  ? formatCurrency(item.totalReturnAmount, bussiness?.currency)
                  : formatCurrency(
                      grandTotal -
                        totalAdditionalDiscount +
                        totalAdditionalFee -
                        (item.subscriptionDisc || 0) -
                        parseFloat(item.advancePayment || 0),
                      bussiness?.currency
                    )}
              </td>
            </tr>
            {item.remainingTransactionAmount > 0 && (
              <>
                <tr>
                  <td className='py-1'>
                    Received: {formatCurrency(paidAmount, bussiness?.currency)}
                  </td>
                </tr>
                <tr>
                  <td className='py-1'>
                    Due Balance:{" "}
                    {formatCurrency(remainingBalance, bussiness?.currency)}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className='border-t pt-2'>
        <p className='text-sm'>
          <strong>Terms:</strong> Goods once sold will not be taken back.
          Payment due within 30 days.
        </p>
        {item.notes && (
          <p className='text-sm'>
            <strong>Notes:</strong> {item.notes}
          </p>
        )}
        <p className='text-sm text-right'>For {bussiness.businessName}</p>
        <div className='flex justify-between items-center mt-2'>
          <div>
            {bussiness?.aadharPhoto ? (
              <img
                src={AWS_URL + bussiness.aadharPhoto}
                alt='Payment QR Code'
                className='w-20 h-20'
                // crossOrigin='anonymous'
              />
            ) : (
              <span className='text-sm'>No QR Code</span>
            )}
          </div>
          <div className='text-right'>
            {bussiness?.signature ? (
              <img
                src={AWS_URL + bussiness.signature}
                alt='Signature'
                className='w-24 h-16 inline-block'
                // crossOrigin='anonymous'
              />
            ) : (
              <span className='text-sm'>Signature</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TraditionalInvoice;
